import React from "react";
import PropTypes from "prop-types";
import { SFStack } from "../grid";
import getStyled from "../../hooks/getStyled";
import {
  SFOfflineBoltOutlinedIcon,
  SFTipsAndUpdatesOutlinedIcon,
  SFCloseIcon,
} from "../icons";
import SFTypography from "../typography";
import SFButton from "../button";
import SFIconButton from "../button/iconButton";

const { styled } = getStyled();

const INLINE_MESSAGE_BLUE =
  "var(--gradient-primary-70060045-deg, linear-gradient(45deg, #404968 0%, #4A5578 100%))";

const Container = styled(SFStack, {
  shouldForwardProp: (prop) =>
    prop !== "fullWidth" && prop !== "disableGutters",
})(({ theme, fullWidth, disableGutters }) => ({
  padding: "15px 20px",
  borderRadius: "4px",
  border: `1px solid ${INLINE_MESSAGE_BLUE}`,
  background: INLINE_MESSAGE_BLUE,
  boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
  width: fullWidth ? "100%" : "auto",
  ...(!disableGutters && { margin: theme.spacing(2.5), marginBottom: 0 }),
}));

const SFIconButtonStyled = styled(SFIconButton)({
  "&:hover": {
    backgroundColor: "transparent",
  },
});

const TEXT_DIRECTION = {
  COLUMN: "column",
  ROW: "row",
};

const VARIANT = {
  BOLT: "bolt",
  BULB: "bulb",
};

const VARIANT_TO_ICON = {
  [VARIANT.BOLT]: SFOfflineBoltOutlinedIcon,
  [VARIANT.BULB]: SFTipsAndUpdatesOutlinedIcon,
};

const SFEmptyStateNotification = ({
  text,
  className,
  supportingText,
  buttonProps,
  fullWidth,
  textDirection,
  variant,
  disableGutters,
  hasCloseButton = true,
  onClose,
}) => {
  const [isVisible, setIsVisible] = React.useState(true);

  if (!isVisible) return null;

  const Icon = VARIANT_TO_ICON[variant];

  const shouldRenderButton =
    typeof buttonProps === "object" && Object.keys(buttonProps).length > 0;

  const closeHandler = () => {
    setIsVisible((prev) => !prev);
    if (onClose) {
      onClose();
    }
  };

  return (
    <Container
      direction="row"
      spacing={hasCloseButton ? 10 : 0}
      alignItems="center"
      justifyContent="space-between"
      fullWidth={fullWidth}
      disableGutters={disableGutters}
      className={className}
    >
      <SFStack direction="row" spacing={2} alignItems="center">
        <Icon sx={{ fontSize: "32px" }} color="white" />
        <SFStack
          direction={textDirection}
          alignItems={
            textDirection === TEXT_DIRECTION.ROW ? "center" : "flex-start"
          }
          spacing={0.75}
        >
          <SFTypography variant="body1bold" color="white">
            {text}
          </SFTypography>
          {supportingText && (
            <SFTypography variant="body1" color="grey100" hasHtml>
              {supportingText}
            </SFTypography>
          )}
        </SFStack>
      </SFStack>
      <SFStack direction="row" alignItems="center" spacing={1.5}>
        {shouldRenderButton && (
          <SFButton
            variant="secondary"
            onClick={buttonProps.onClick}
            icon={buttonProps.startIcon}
            endIcon={buttonProps.endIcon}
            disabled={buttonProps.disabled}
          >
            <SFTypography variant="buttonDefault">
              {buttonProps.label}
            </SFTypography>
          </SFButton>
        )}

        {hasCloseButton && (
          <SFIconButtonStyled size="small" onClick={closeHandler}>
            <SFCloseIcon size="small" color="white" />
          </SFIconButtonStyled>
        )}
      </SFStack>
    </Container>
  );
};

export default SFEmptyStateNotification;

SFEmptyStateNotification.defaultProps = {
  buttonProps: {},
  fullWidth: false,
  textDirection: TEXT_DIRECTION.ROW,
  variant: VARIANT.BULB,
  disableGutters: false,
};

SFEmptyStateNotification.propTypes = {
  /**
   * The main message to display in the empty state notification (medium).
   */
  text: PropTypes.string.isRequired,
  /**
   * The secondary message to display in the empty state notification (regular).
   */
  supportingText: PropTypes.string,
  /**
   * The props to pass to the button in the empty state notification. The button will only be displayed if these props are provided.
   */
  buttonProps: PropTypes.shape({
    label: PropTypes.string,
    onClick: PropTypes.func,
  }),
  /**
   * Whether the empty state notification should take up the full width of its parent container.
   */
  fullWidth: PropTypes.bool,
  /**
   * Determines the relative positioning of the text and supporting text in the empty state notification.
   */
  textDirection: PropTypes.oneOf([TEXT_DIRECTION.COLUMN, TEXT_DIRECTION.ROW]),
  /**
   * Determines the icon to display in the empty state notification.
   */
  variant: PropTypes.oneOf([VARIANT.BOLT, VARIANT.BULB]),
  /**
   * Whether to remove the default margin from the empty state notification.
   */
  disableGutters: PropTypes.bool,
  /**
   * Whether to show the close button in the empty state notification.
   */
  hasCloseButton: PropTypes.bool,
  /**
   * Callback that executes when user closes the banner.
   */
  onClose: PropTypes.func,
  /**
   * The class name to apply to the empty state notification.
   * This is useful for applying custom styles or overriding default styles.
   */
  className: PropTypes.object,
};
